import React, { useMemo, useState, useEffect } from 'react';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { useAuthStore } from '../store/authStore';

type FeedbackCategory = 'issue' | 'feature' | 'feedback';

export const FeedbackModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuthStore();
  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // Honeypot - must stay empty

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const canSubmit = useMemo(() => {
    const hasCategory = !!category;
    const hasMessage = message.trim().length >= 10 && message.trim().length <= 2000;
    const hasEmail = isAuthenticated ? true : !!email.trim();
    return hasCategory && hasMessage && hasEmail && !loading && !website;
  }, [category, message, email, isAuthenticated, loading, website]);

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
    } else if (!isOpen) {
      setEmail('');
    }
  }, [isAuthenticated, user?.email, isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const reset = () => {
    setCategory('');
    setMessage('');
    if (!isAuthenticated) setEmail('');
    setWebsite('');
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!category || message.trim().length < 10 || message.trim().length > 2000) {
      setError('Please provide a category and message (10–2000 characters).');
      return;
    }
    if (!isAuthenticated && !email.trim()) {
      setError('Email is required for anonymous feedback.');
      return;
    }
    if (website) {
      return; // Honeypot filled - silent reject
    }

    setLoading(true);
    try {
      await api.post('/feedback', {
        category,
        message: message.trim(),
        email: isAuthenticated ? undefined : email.trim(),
        website: website || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to submit feedback'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      <div className="relative w-full max-w-xl max-h-[85vh] rounded-2xl bg-white shadow-2xl border border-purple-200 overflow-hidden flex flex-col">
        <div className="p-5 sm:p-6 bg-gradient-to-br from-purple-600 via-purple-500 to-purple-400 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <div className="text-2xl font-black text-white">Beta Feedback</div>
              <div className="text-purple-100 text-sm">Report issues, request features, or share feedback</div>
            </div>
          </div>
          <button onClick={handleClose} className="text-white/90 hover:text-white transition-colors" aria-label="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 sm:p-6 overflow-y-auto bg-white">
          {success ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5">
              <div className="text-lg font-black text-slate-900">Thanks for your feedback!</div>
              <div className="text-emerald-700 text-sm mt-1">
                We&apos;ll review your submission and get back to you if needed.
              </div>
              <button
                onClick={handleClose}
                className="mt-5 w-full py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold mb-2 text-slate-700">
                  Category <span className="text-purple-600">*</span>
                </label>
                <select
                  className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
                  required
                >
                  <option value="" disabled>Select a category</option>
                  <option value="issue">Report an issue</option>
                  <option value="feature">Request a feature</option>
                  <option value="feedback">General feedback</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-slate-700">
                  Message <span className="text-purple-600">*</span>
                </label>
                <textarea
                  className="w-full min-h-[120px] rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-y"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your issue, feature request, or feedback (10–2000 characters)"
                  required
                  minLength={10}
                  maxLength={2000}
                />
                <div className="text-xs text-slate-500 mt-1">{message.length}/2000</div>
              </div>

              {!isAuthenticated && (
                <div>
                  <label className="block text-sm font-bold mb-2 text-slate-700">
                    Email <span className="text-purple-600">*</span>
                  </label>
                  <input
                    className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-slate-800 placeholder:text-slate-400 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    type="email"
                    required
                  />
                </div>
              )}

              {/* Honeypot - hidden from users, bots often fill it */}
              <div className="absolute -left-[9999px] w-1 h-1 overflow-hidden" aria-hidden="true">
                <label htmlFor="feedback-website">Website</label>
                <input
                  id="feedback-website"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting…' : 'Send Feedback'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
